<template>
  <div
    class="schedule-messages"
  >
    <h3 class="schedule-messages__header">Schedule new message</h3>
    <label class="schedule-messages__input">
      Channel (#name or channel_id):
      <input
        v-model="channel"
        placeholder="channel"
      />
      {{ getChannelName(channel) }}
    </label>
    <label class="schedule-messages__input">
      Timestamp:
      <input
        v-model="timestamp"
        placeholder="timestamp"
        type="datetime-local"
      />
      ({{ timestampFormatted }})
    </label>
    <label class="schedule-messages__as-user">
      AsUser:
      <input
        v-model="asUser"
        type="checkbox"
      />
      (If checked the message will be posted as you, otherwise this app posts the message)
    </label>
    <label class="schedule-messages__input">
      Text:
      <br>
      <small>(Find formatting infos <a href="https://api.slack.com/reference/surfaces/formatting#advanced"
                                       target="_blank">here</a>)</small>
      <br>
      <textarea
        v-model="text"
        placeholder="text"
        style="width: 100%; height: 6rem;"
      />
    </label>
    <button @click="sendMessage">send</button>

    <hr class="schedule-messages__hr">

    <h3 class="schedule-messages__header">Message Templates</h3>
    <div class="d-flex">
      <div class="w-25">
        <label class="schedule-messages__input">
          <select v-model="selectedTemplate">
            <option
              v-for="(template, index) in templates"
              :key="index"
              :value="template"
            >{{ template.name }}
            </option>
          </select>
        </label>
        <button @click="loadTemplate(selectedTemplate)">Load template</button>
      </div>
      <div class="flex-1" v-if="selectedTemplate">
        <div><strong>Time of day:</strong> {{ selectedTemplate.timeOfDay }}</div>
        <div><strong>Channel:</strong> {{ selectedTemplate.channel }}</div>
        <div><strong>AsUser:</strong> {{ selectedTemplate.asUser }}</div>
        <div><strong>Text:</strong> {{ selectedTemplate.text }}</div>
      </div>
    </div>

    <hr class="schedule-messages__hr">

    <h3 class="schedule-messages__header">Already scheduled messages</h3>
    <button @click="updateScheduledMessages">refresh</button>
    <ul>
      <li
        v-for="scheduledMessage in scheduledMessages"
        :key="scheduledMessage.id"
        class="schedule-messages__message"
      >
        <strong>message_id:</strong> {{ scheduledMessage.id }}<br>
        <strong>channel_id:</strong> {{ scheduledMessage.channel_id }} - {{ getChannelName(scheduledMessage.channel_id)
        }}<br>
        <strong>post_at:</strong> {{ getDateFormatted(scheduledMessage.post_at) }}<br>
        <strong>text:</strong><br>{{ scheduledMessage.text }}
        <button @click="deleteMessage(scheduledMessage.channel_id, scheduledMessage.id)">delete message</button>
      </li>
    </ul>

    <hr class="schedule-messages__hr">

    <h3 class="schedule-messages__header">Available channels</h3>
    <button @click="updateChannels">refresh</button>
    <ul>
      <li
        v-for="channel in channels"
        :key="channel.id"
      >
        {{ channel.id }} - #{{ channel.name }}
      </li>
    </ul>
  </div>
</template>

<script>
import {
  getChannels,
  sendMessage,
  deleteMessage,
  getScheduledMessages,
} from '@/data';

export default {
  name: 'ScheduleMessages',
  data() {
    return {
      channel: null,
      timestamp: null,
      asUser: false,
      text: null,

      channels: null,
      scheduledMessages: null,

      templates: [
        {
          name: 'Webdev standup preview',
          channel: '#webdev',
          timeOfDay: '11:45',
          asUser: false,
          text: 'Hello <!channel>, standup at 12:00 in https://hangouts.google.com/call/i-evVryMEMzGUbkwtBr5ACEI',
        },
        {
          name: 'Webdev standup ontime',
          channel: '#webdev',
          timeOfDay: '12:00',
          asUser: false,
          text: 'Hello <!channel>, standup now :clock12:',
        },
        {
          name: 'Coffee break',
          channel: '#devteam',
          timeOfDay: '14:45',
          asUser: false,
          text: 'Hello <!here>, voluntary coffee break meeting at 15:00 in https://hangouts.google.com/call/yX7eaCfigQLj0L_ZG8zwAEEI',
        },
        {
          name: 'Devteam standup preview',
          channel: '#devteam',
          timeOfDay: '11:45',
          asUser: false,
          text: 'Hello <!channel>, standup at 12:00 in https://hangouts.google.com/call/yX7eaCfigQLj0L_ZG8zwAEEI',
        },
        {
          name: 'Devteam standup ontime',
          channel: '#devteam',
          timeOfDay: '12:00',
          asUser: false,
          text: 'Hello <!channel>, standup now :clock12:',
        },
      ],
      selectedTemplate: null,
    };
  },
  created() {
    this.updateChannels();
    this.updateScheduledMessages();
    this.selectedTemplate = this.templates[0];
    this.loadTemplate(this.selectedTemplate);
  },
  methods: {
    async sendMessage() {
      let date = new Date(this.timestamp);
      await sendMessage(this.channel, date, this.asUser, this.text);
      await this.updateScheduledMessages();
      date = this.getNextWeekdayFromDate(date);
      this.setTimestampFromDate(date);
    },
    async deleteMessage(channelId, messageId) {
      await deleteMessage(channelId, messageId);
      await this.updateScheduledMessages();
    },
    async updateScheduledMessages() {
      this.scheduledMessages = null;
      const scheduledMessages = await getScheduledMessages();
      this.scheduledMessages = scheduledMessages.sort((msg1, msg2) => msg1.post_at - msg2.post_at);
    },
    async updateChannels() {
      this.channels = null;
      this.channels = await getChannels();
    },
    loadTemplate(template) {
      this.text = template.text;
      this.asUser = template.asUser;
      this.channel = template.channel;

      let targetDate = new Date();
      const timeOfDayParts = template.timeOfDay.split(':');
      targetDate.setHours(parseInt(timeOfDayParts[0], 10));
      targetDate.setMinutes(parseInt(timeOfDayParts[1], 10));
      targetDate.setSeconds(0);
      if (targetDate < new Date()) {
        targetDate = this.getNextWeekdayFromDate(targetDate);
      }
      this.setTimestampFromDate(targetDate);
    },
    setTimestampFromDate(date) {
      const leftPad = curVal => ('' + curVal).padStart(2, '0');
      const year = date.getFullYear();
      const month = leftPad(date.getMonth() + 1);
      const day = leftPad(date.getDate());
      const hours = leftPad(date.getHours());
      const minutes = leftPad(date.getMinutes());
      this.timestamp = `${[year, month, day].join('-')}T${hours}:${minutes}`;
    },
    getChannelName(id) {
      if (typeof id === 'string' && id.charAt(0) === '#') {
        return '';
      }
      let name = 'invalid channel';
      if (this.channels == null) {
        return name;
      }
      this.channels.forEach((channel) => {
        if (channel.id === id) {
          name = `#${channel.name}`;
        }
      });
      return name;
    },
    getDateFormatted(timestamp) {
      return new Date(timestamp * 1000).toString();
    },
    getNextWeekdayFromDate(date) {
      let currentDate = date;
      do {
        currentDate = new Date(currentDate.getTime() + (24 * 60 * 60 * 1000));
      } while (currentDate.getDay() === 6 || currentDate.getDay() === 0);
      return currentDate;
    },
  },
  computed: {
    timestampFormatted() {
      if (this.timestamp == null) {
        return 'missing timestamp';
      }
      return new Date(this.timestamp).toString();
    },
  },
};
</script>

<style scoped>
.schedule-messages {
  padding-top: 20px;
}

.schedule-messages__message {
  margin: 20px 0;
}

.schedule-messages__hr {
  margin: 50px 0;
}

.schedule-messages__input {
  display: block;
  margin: 10px 0;
}
</style>



// WEBPACK FOOTER //
// src/components/ScheduleMessages.vue